(() => {
  const init = (root) => {
    if (!root || root.dataset.luBsInit === 'true') return;
    root.dataset.luBsInit = 'true';

    const toggle = root.querySelector('[data-lu-menu-toggle]');
    const drawer = root.querySelector('[data-lu-mobile-drawer]');
    const overlay = root.querySelector('[data-lu-menu-overlay]');
    const closeBtn = root.querySelector('[data-lu-menu-close]');
    const searchLayer = root.querySelector('[data-lu-search-layer]');
    const searchToggles = root.querySelectorAll('[data-lu-search-toggle]');
    const searchClosers = root.querySelectorAll('[data-lu-search-close]');
    const searchInput = root.querySelector('[data-lu-search-input]');

    const openMenu = () => {
      root.classList.add('lu-bs-mobile-open');
      document.documentElement.classList.add('lu-bs-lock');
      document.body.classList.add('lu-bs-menu-open');
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
      if (drawer) drawer.setAttribute('aria-hidden', 'false');
    };

    const closeMenu = () => {
      root.classList.remove('lu-bs-mobile-open');
      document.documentElement.classList.remove('lu-bs-lock');
      document.body.classList.remove('lu-bs-menu-open');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
      if (drawer) drawer.setAttribute('aria-hidden', 'true');
    };

    const updateDesktopSearchPosition = () => {
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

      const desktopBar = root.querySelector('.lu-bs-header__bar');
      if (!desktopBar) return;

      const rect = desktopBar.getBoundingClientRect();
      const top = Math.max(0, Math.round(rect.bottom));

      // One shared, exact anchor: every desktop dropdown starts flush at the header bottom.
      root.style.setProperty('--lu-bs-search-top', `${top}px`);
      root.style.setProperty('--lu-bs-dropdown-top', `${top}px`);

      // Compact info panels stay visually below their own navigation item
      // while still being fixed to the exact header-bottom line.
      root.querySelectorAll('.lu-bs-nav__item.has-info-panel').forEach((item) => {
        const panel = item.querySelector(':scope > .lu-bs-info-menu');
        if (!panel) return;

        const itemRect = item.getBoundingClientRect();
        const panelWidth = parseFloat(getComputedStyle(panel).width) || 360;
        const edge = 12;
        const half = panelWidth / 2;
        let center = itemRect.left + (itemRect.width / 2);

        center = Math.max(half + edge, Math.min(window.innerWidth - half - edge, center));
        item.style.setProperty('--lu-info-left', `${Math.round(center)}px`);
      });
    };

    const openSearch = () => {
      closeMenu();
      updateDesktopSearchPosition();
      root.classList.add('lu-bs-search-open');
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        document.documentElement.classList.add('lu-bs-search-lock');
      } else {
        document.documentElement.classList.remove('lu-bs-search-lock');
      }
      searchToggles.forEach((button) => button.setAttribute('aria-expanded', 'true'));
      if (searchLayer) searchLayer.setAttribute('aria-hidden', 'false');
      window.setTimeout(() => searchInput?.focus({ preventScroll: true }), 180);
    };

    const closeSearch = () => {
      root.classList.remove('lu-bs-search-open');
      document.documentElement.classList.remove('lu-bs-search-lock');
      searchToggles.forEach((button) => button.setAttribute('aria-expanded', 'false'));
      if (searchLayer) searchLayer.setAttribute('aria-hidden', 'true');
    };

    if (toggle) toggle.addEventListener('click', openMenu);
    searchToggles.forEach((button) => button.addEventListener('click', openSearch));
    searchClosers.forEach((button) => button.addEventListener('click', closeSearch));

    let hoverCloseTimer;
    const desktopSearchToggle = root.querySelector('.lu-bs-search-toggle');
    const searchDrawer = root.querySelector('.lu-bs-search-drawer');

    const fitDesktopHeader = () => {
      // Keep desktop logo and navigation sizing deterministic.
      root.classList.remove('lu-bs-compact', 'lu-bs-ultra-compact', 'lu-bs-force-mobile');
    };

    const scheduleFit = () => {
      fitDesktopHeader();
    };

    scheduleFit();
    updateDesktopSearchPosition();

    let luHeaderFrame = 0;
    const scheduleHeaderPositionUpdate = (includeFit = false) => {
      if (luHeaderFrame) return;
      luHeaderFrame = window.requestAnimationFrame(() => {
        luHeaderFrame = 0;
        if (includeFit) scheduleFit();
        updateDesktopSearchPosition();
      });
    };

    window.addEventListener('resize', () => {
      scheduleHeaderPositionUpdate(true);
    }, { passive: true });

    window.addEventListener('scroll', () => {
      scheduleHeaderPositionUpdate(false);
    }, { passive: true });

    window.addEventListener('load', () => {
      scheduleFit();
      updateDesktopSearchPosition();
    }, { once: true });

    const cancelHoverClose = () => {
      if (hoverCloseTimer) {
        window.clearTimeout(hoverCloseTimer);
        hoverCloseTimer = undefined;
      }
    };

    const scheduleHoverClose = () => {
      cancelHoverClose();
      hoverCloseTimer = window.setTimeout(() => {
        if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) closeSearch();
      }, 220);
    };

    if (desktopSearchToggle) {
      desktopSearchToggle.addEventListener('mouseenter', () => {
        if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
        cancelHoverClose();
        openSearch();
      });
      desktopSearchToggle.addEventListener('mouseleave', scheduleHoverClose);
    }

    if (searchDrawer) {
      searchDrawer.addEventListener('mouseenter', cancelHoverClose);
      searchDrawer.addEventListener('mouseleave', scheduleHoverClose);
    }
    if (closeBtn) closeBtn.addEventListener('click', closeMenu);
    if (overlay) overlay.addEventListener('click', closeMenu);

    root.querySelectorAll('.lu-bs-mobile__group').forEach((group) => {
      group.addEventListener('toggle', () => {
        if (!group.open) return;
        root.querySelectorAll('.lu-bs-mobile__group').forEach((other) => {
          if (other !== group) other.removeAttribute('open');
        });
      });
    });

    root.addEventListener('click', (event) => {
      const link = event.target.closest('.lu-bs-mobile__drawer a');
      if (link) closeMenu();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      if (root.classList.contains('lu-bs-search-open')) closeSearch();
      if (root.classList.contains('lu-bs-mobile-open')) closeMenu();
    });
  };

  const initAll = (scope = document) => {
    if (scope.matches && scope.matches('[data-lu-bs-header]')) init(scope);
    scope.querySelectorAll?.('[data-lu-bs-header]').forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();

(() => {
  const debounceSearch = (callback, wait = 180) => {
    let timer;
    return (...args) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => callback(...args), wait);
    };
  };

  const initLUPredictiveSearch = (root) => {
    if (!root || root.dataset.luPredictiveInit === 'true') return;
    root.dataset.luPredictiveInit = 'true';

    const widgets = root.querySelectorAll('[data-lu-search-widget]');

    widgets.forEach((widget, widgetIndex) => {
      const form = widget.querySelector('form[role="search"]');
      const input = form?.querySelector('input[type="search"][name="q"]');
      const panel = widget.querySelector('[data-lu-predictive]');
      const list = widget.querySelector('[data-lu-predictive-list]');

      if (!form || !input || !panel || !list) return;

      let controller;
      let activeIndex = -1;
      const cache = new Map();

      const options = () => Array.from(list.querySelectorAll('.lu-bs-predictive__item'));

      const setActive = (index) => {
        const items = options();
        if (!items.length) {
          activeIndex = -1;
          input.removeAttribute('aria-activedescendant');
          return;
        }

        activeIndex = Math.max(0, Math.min(index, items.length - 1));

        items.forEach((item, itemIndex) => {
          const active = itemIndex === activeIndex;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-selected', active ? 'true' : 'false');
        });

        input.setAttribute('aria-activedescendant', items[activeIndex].id);
        items[activeIndex].scrollIntoView({ block: 'nearest' });
      };

      const closeResults = () => {
        panel.hidden = true;
        input.setAttribute('aria-expanded', 'false');
        input.removeAttribute('aria-activedescendant');
        activeIndex = -1;
      };

      const openResults = () => {
        if (!options().length) {
          closeResults();
          return;
        }
        panel.hidden = false;
        input.setAttribute('aria-expanded', 'true');
      };

      const renderProducts = (products) => {
        list.replaceChildren();
        activeIndex = -1;

        products.slice(0, 6).forEach((product, productIndex) => {
          const item = document.createElement('a');
          item.className = 'lu-bs-predictive__item';
          item.href = product.url;
          item.id = `lu-predictive-option-${widgetIndex}-${productIndex}`;
          item.setAttribute('role', 'option');
          item.setAttribute('aria-selected', 'false');

          const media = document.createElement('span');
          media.className = 'lu-bs-predictive__media';

          const featuredImage = product.featured_image;
          const imageUrl = typeof featuredImage === 'string'
            ? featuredImage
            : featuredImage?.url;

          if (imageUrl) {
            const image = document.createElement('img');
            image.src = imageUrl;
            image.alt = featuredImage?.alt || product.title || '';
            image.loading = 'lazy';
            image.width = 72;
            image.height = 86;
            media.appendChild(image);
          } else {
            const placeholder = document.createElement('span');
            placeholder.className = 'lu-bs-predictive__placeholder';
            placeholder.setAttribute('aria-hidden', 'true');
            media.appendChild(placeholder);
          }

          const copy = document.createElement('span');
          copy.className = 'lu-bs-predictive__copy';

          const title = document.createElement('span');
          title.className = 'lu-bs-predictive__title';
          title.textContent = product.title || '';
          copy.appendChild(title);

          const metaText = product.vendor || product.type || product.product_type || '';
          if (metaText) {
            const meta = document.createElement('span');
            meta.className = 'lu-bs-predictive__meta';
            meta.textContent = metaText;
            copy.appendChild(meta);
          }

          const arrow = document.createElement('span');
          arrow.className = 'lu-bs-predictive__arrow';
          arrow.setAttribute('aria-hidden', 'true');
          arrow.textContent = '›';

          item.append(media, copy, arrow);
          list.appendChild(item);
        });

        openResults();
      };

      const fetchProducts = async (query) => {
        const normalizedQuery = query.trim();
        if (!normalizedQuery) {
          closeResults();
          return;
        }

        if (cache.has(normalizedQuery)) {
          renderProducts(cache.get(normalizedQuery));
          return;
        }

        if (controller) controller.abort();
        controller = new AbortController();

        input.setAttribute('aria-busy', 'true');
        panel.classList.add('is-loading');

        try {
          const rootUrl = window.Shopify?.routes?.root || '/';
          const endpoint = `${rootUrl}search/suggest.json`;
          const params = new URLSearchParams({
            q: normalizedQuery,
            'resources[type]': 'product',
            'resources[limit]': '6',
            'resources[options][unavailable_products]': 'last'
          });

          const response = await fetch(`${endpoint}?${params.toString()}`, {
            headers: { Accept: 'application/json' },
            signal: controller.signal
          });

          if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);

          const data = await response.json();
          const products = data?.resources?.results?.products || [];

          cache.set(normalizedQuery, products);

          if (input.value.trim() === normalizedQuery) {
            renderProducts(products);
          }
        } catch (error) {
          if (error.name !== 'AbortError') closeResults();
        } finally {
          input.removeAttribute('aria-busy');
          panel.classList.remove('is-loading');
        }
      };

      const search = debounceSearch(() => fetchProducts(input.value), 180);

      input.addEventListener('input', () => {
        if (!input.value.trim()) {
          if (controller) controller.abort();
          closeResults();
          list.replaceChildren();
          return;
        }
        search();
      });

      input.addEventListener('focus', () => {
        if (input.value.trim()) fetchProducts(input.value);
      });

      input.addEventListener('keydown', (event) => {
        const items = options();

        if (event.key === 'Escape') {
          closeResults();
          return;
        }

        if (!items.length || panel.hidden) return;

        if (event.key === 'ArrowDown') {
          event.preventDefault();
          setActive(activeIndex < items.length - 1 ? activeIndex + 1 : 0);
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          setActive(activeIndex > 0 ? activeIndex - 1 : items.length - 1);
        } else if (event.key === 'Enter' && activeIndex >= 0) {
          event.preventDefault();
          window.location.href = items[activeIndex].href;
        }
      });

      form.addEventListener('submit', (event) => {
        const query = input.value.trim();
        if (!query) {
          event.preventDefault();
          input.focus();
          return;
        }
        input.value = query;
        closeResults();
      });

      document.addEventListener('click', (event) => {
        if (!widget.contains(event.target)) closeResults();
      });

      root.querySelectorAll('[data-lu-search-close]').forEach((button) => {
        button.addEventListener('click', closeResults);
      });
    });
  };

  const initAllPredictive = (scope = document) => {
    if (scope.matches?.('[data-lu-bs-header]')) initLUPredictiveSearch(scope);
    scope.querySelectorAll?.('[data-lu-bs-header]').forEach(initLUPredictiveSearch);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAllPredictive());
  } else {
    initAllPredictive();
  }

  document.addEventListener('shopify:section:load', (event) => initAllPredictive(event.target));
})();
